/** RSI midline scoring + context-aware divergence (api/scan.js + App.jsx). */

export function calcRSI(closes, period = 14) {
  if (!closes?.length || closes.length < period + 1) return null;
  let gains = 0;
  let losses = 0;
  for (let i = 0; i < period; i++) {
    const d = closes[i] - closes[i + 1];
    if (d >= 0) gains += d;
    else losses -= d;
  }
  let avgGain = gains / period;
  let avgLoss = losses / period;
  for (let i = period; i < closes.length - 1; i++) {
    const d = closes[i] - closes[i + 1];
    avgGain = (avgGain * (period - 1) + Math.max(d, 0)) / period;
    avgLoss = (avgLoss * (period - 1) + Math.max(-d, 0)) / period;
  }
  if (avgLoss === 0) return 100;
  const rs = avgGain / avgLoss;
  return 100 - 100 / (1 + rs);
}

function emaLatest(closes, period) {
  if (!closes?.length || closes.length < period) return null;
  const chrono = [...closes].reverse();
  let ema = chrono.slice(0, period).reduce((s, v) => s + v, 0) / period;
  const k = 2 / (period + 1);
  for (let i = period; i < chrono.length; i++) {
    ema = chrono[i] * k + ema * (1 - k);
  }
  return ema;
}

function calcCMF(highs, lows, closes, volumes, n = 20) {
  if (!closes?.length || closes.length < n || !volumes?.length) return null;
  let mfv = 0;
  let vol = 0;
  for (let i = 0; i < n; i++) {
    const hl = highs[i] - lows[i];
    if (!hl) continue;
    mfv += ((closes[i] - lows[i]) - (highs[i] - closes[i])) / hl * (volumes[i] || 0);
    vol += volumes[i] || 0;
  }
  return vol ? mfv / vol : 0;
}

function donchian20(highs, lows, closes) {
  if (!highs?.length || highs.length < 21) {
    return { upper: null, lower: null, breakout: false, breakdown: false };
  }
  const upper = Math.max(...highs.slice(1, 21));
  const lower = Math.min(...lows.slice(1, 21));
  const price = closes?.[0];
  return {
    upper,
    lower,
    breakout: price != null && price > upper,
    breakdown: price != null && price < lower,
  };
}

function justCrossedAboveEma(closes, period, lookback = 3) {
  if (!closes?.length || closes.length < period + lookback + 1) return false;
  for (let i = 0; i < lookback; i++) {
    const slice = closes.slice(i);
    const ema = emaLatest(slice, period);
    const price = closes[i];
    const prevPrice = closes[i + 1];
    const prevEma = emaLatest(closes.slice(i + 1), period);
    if (ema != null && prevEma != null && prevPrice <= prevEma && price > ema) return true;
  }
  return false;
}

/** 10-bar higher high + RSI lower high (raw pattern). */
function rawBearishPattern10(closes, highs, lows, period = 14) {
  if (!closes?.length || closes.length < 12) return false;
  const h = highs?.length ? highs : closes;
  const recentHigh = Math.max(...h.slice(0, 3));
  const refHigh = h[10] != null ? Math.max(h[10], ...(h.slice(10, 13).length ? h.slice(10, 13) : [h[10]])) : null;
  if (refHigh == null || recentHigh <= refHigh) return false;
  const rsiNow = calcRSI(closes, period);
  const rsiRef = calcRSI(closes.slice(10), period);
  return rsiNow != null && rsiRef != null && rsiNow < rsiRef;
}

function rawBullishPattern10(closes, highs, lows, period = 14) {
  if (!closes?.length || closes.length < 12) return false;
  const l = lows?.length ? lows : closes;
  const recentLow = Math.min(...l.slice(0, 3));
  const refLow = l[10] != null ? Math.min(l[10], ...(l.slice(10, 13).length ? l.slice(10, 13) : [l[10]])) : null;
  if (refLow == null || recentLow >= refLow) return false;
  const rsiNow = calcRSI(closes, period);
  const rsiRef = calcRSI(closes.slice(10), period);
  return rsiNow != null && rsiRef != null && rsiNow > rsiRef;
}

function volumeDeclining5WhilePriceRises(closes, volumes) {
  if (!volumes?.length || volumes.length < 5 || !closes?.length || closes.length < 5) return false;
  for (let i = 0; i < 4; i++) {
    if (!(volumes[i] < volumes[i + 1])) return false;
  }
  return closes[0] > closes[4];
}

/**
 * Context-aware RSI divergence — used by scan + runAnalysis.
 * @param {object} context — volumes, cmf, price, priorBuyScore, currentBuyScore, donchianBreakout
 */
export function computeContextAwareRsiDivergence(closes, highs, lows, context = {}) {
  const empty = {
    rsiBullishDivergence: false,
    rsiBearishDivergence: false,
    bullish: false,
    bearish: false,
    rsiDivergenceContext: "No RSI divergence — pattern or context filters not met.",
  };
  if (!closes?.length) return empty;

  const volumes = context.volumes;
  const cmf = context.cmf ?? (highs?.length && lows?.length && volumes?.length
    ? calcCMF(highs, lows, closes, volumes)
    : null);
  const price = context.price ?? closes[0];
  const don = context.donchianBreakout != null
    ? { breakout: context.donchianBreakout === true, breakdown: context.donchianBreakdown === true }
    : donchian20(highs, lows, closes);

  const ema50 = emaLatest(closes, 50);
  const ema200 = emaLatest(closes, 200);
  const cross50 = justCrossedAboveEma(closes, 50);
  const cross200 = justCrossedAboveEma(closes, 200);

  const priorBuyScore = context.priorBuyScore;
  const currentBuyScore = context.currentBuyScore;
  const scoreAccel =
    priorBuyScore != null && currentBuyScore != null
    && (currentBuyScore - priorBuyScore) > 3;

  const move20d = closes.length > 20 && closes[20] > 0
    ? ((closes[0] - closes[20]) / closes[20]) * 100
    : null;
  const high52 = highs?.length ? Math.max(...highs.slice(0, Math.min(252, highs.length))) : null;
  const near52High = high52 != null && price != null && price >= high52 * 0.98;
  const weakCmfAtHighs = cmf != null && cmf < 0.05;
  const extendedRun = move20d != null && move20d > 15;
  const volDeclineRise = volumeDeclining5WhilePriceRises(closes, volumes);
  const extendedOk = extendedRun || (near52High && weakCmfAtHighs) || volDeclineRise;

  let rsiBearishDivergence = false;
  let bearContext = "";
  if (rawBearishPattern10(closes, highs, lows)) {
    if (don.breakout) {
      bearContext = "Bearish RSI pattern ignored — confirmed Donchian breakout (momentum leg).";
    } else if (cross50 || cross200) {
      bearContext = "Bearish RSI pattern ignored — price just reclaimed EMA50/EMA200 (new structure forming).";
    } else if (scoreAccel) {
      bearContext = "Bearish RSI pattern ignored — score jumped >3 since last scan (momentum accelerating).";
    } else if (!extendedOk) {
      bearContext = "Bearish RSI pattern present but not extended enough (need 20-session run >15%, near 52W high + weak CMF, or 5-day vol decline into rising price).";
    } else {
      rsiBearishDivergence = true;
      bearContext = extendedRun
        ? `Bearish divergence — higher high with lower RSI after extended ${move20d.toFixed(0)}% 20-session run.`
        : near52High && weakCmfAtHighs
        ? "Bearish divergence — fading RSI near 52-week high with weak CMF (<0.05)."
        : "Bearish divergence — price rising on declining volume for 5 sessions.";
    }
  }

  let rsiBullishDivergence = false;
  let bullContext = "";
  if (rawBullishPattern10(closes, highs, lows)) {
    if (don.breakdown) {
      bullContext = "Bullish RSI pattern ignored — Donchian lower channel break (breakdown).";
    } else if (ema50 != null && price != null && price >= ema50) {
      bullContext = "Bullish RSI pattern ignored — price above EMA50 (not a downtrend pullback).";
    } else {
      rsiBullishDivergence = true;
      bullContext = "Bullish divergence — lower low with higher RSI below EMA50; selling may be exhausting.";
    }
  }

  let rsiDivergenceContext = "No RSI divergence — pattern or context filters not met.";
  if (rsiBearishDivergence && rsiBullishDivergence) {
    rsiDivergenceContext = `${bearContext} | ${bullContext}`;
  } else if (rsiBearishDivergence) {
    rsiDivergenceContext = bearContext;
  } else if (rsiBullishDivergence) {
    rsiDivergenceContext = bullContext;
  } else if (bearContext) {
    rsiDivergenceContext = bearContext;
  } else if (bullContext) {
    rsiDivergenceContext = bullContext;
  }

  return {
    rsiBullishDivergence,
    rsiBearishDivergence,
    bullish: rsiBullishDivergence,
    bearish: rsiBearishDivergence,
    rsiDivergenceContext,
  };
}

/** Legacy 20-bar swing scan — kept for reference; prefer context-aware. */
export function computeRsiDivergence(closes, highs, lows, period = 14, lookback = 20) {
  return computeContextAwareRsiDivergence(closes, highs, lows, {});
}

export function calcRsiMidlineScore(closes, period = 14) {
  const rsi0 = calcRSI(closes, period);
  if (rsi0 == null) return 0;
  const rsi1 = closes.length > period + 2 ? calcRSI(closes.slice(1), period) : null;

  if (rsi1 != null && rsi1 < 50 && rsi0 >= 50) return 2;
  if (rsi1 != null && rsi1 >= 50 && rsi0 < 50) return -2;
  if (rsi0 > 50 && rsi1 != null && rsi0 > rsi1) return 1;
  if (rsi0 < 50 && rsi1 != null && rsi0 < rsi1) return -1;
  if (rsi0 < 30) return 1;
  return 0;
}

export function buildRsiScoring(closes, highs, lows, context = {}) {
  const rsi = calcRSI(closes);
  const div = computeContextAwareRsiDivergence(closes, highs, lows, context);
  const rsiMidlineScore = calcRsiMidlineScore(closes);
  const divScore = div.rsiBullishDivergence ? 2 : div.rsiBearishDivergence ? -2 : 0;
  return {
    rsi,
    rsiDiv: div,
    rsiMidlineScore,
    rsiDivergenceScore: divScore,
    rsiScore: rsiMidlineScore + divScore,
  };
}

export function attachRsiScoring(payload) {
  if (!payload?.closes?.length) return payload;
  const context = {
    volumes: payload.volumes,
    cmf: payload.cmf,
    price: payload.livePrice ?? payload.closes[0],
    priorBuyScore: payload.priorBuyScore != null ? Number(payload.priorBuyScore) : null,
    currentBuyScore: payload.currentBuyScore != null ? Number(payload.currentBuyScore) : null,
    donchianBreakout: payload.donchianBreakout,
    donchianBreakdown: payload.donchianBreakdown,
  };
  const built = buildRsiScoring(payload.closes, payload.highs, payload.lows, context);
  payload.rsi = built.rsi;
  payload.rsiDiv = built.rsiDiv;
  payload.rsiMidlineScore = built.rsiMidlineScore;
  payload.rsiDivergenceScore = built.rsiDivergenceScore;
  payload.rsiScore = built.rsiScore;
  payload.rsiBullishDivergence = built.rsiDiv.rsiBullishDivergence;
  payload.rsiBearishDivergence = built.rsiDiv.rsiBearishDivergence;
  payload.rsiDivergenceContext = built.rsiDiv.rsiDivergenceContext;

  if (!Array.isArray(payload.signalReasons)) payload.signalReasons = [];
  if (built.rsiDiv.rsiBullishDivergence && !payload.signalReasons.includes("RSI bullish divergence")) {
    payload.signalReasons.push("RSI bullish divergence");
  }
  if (built.rsiDiv.rsiBearishDivergence && !payload.signalReasons.includes("RSI bearish divergence")) {
    payload.signalReasons.push("RSI bearish divergence");
  }
  return payload;
}

/** Re-evaluate divergence after client-side buyScore (rule 5). */
export function refreshRsiDivergenceForScore(result, priorBuyScore) {
  if (!result?.closes?.length) return result;
  const div = computeContextAwareRsiDivergence(result.closes, result.highs, result.lows, {
    volumes: result.volumes,
    cmf: result.cmf,
    price: result.price,
    priorBuyScore,
    currentBuyScore: result.buyScore,
  });
  result.rsiDiv = div;
  result.rsiBullishDivergence = div.rsiBullishDivergence;
  result.rsiBearishDivergence = div.rsiBearishDivergence;
  result.rsiDivergenceContext = div.rsiDivergenceContext;
  result.rsiDivergenceScore = div.rsiBullishDivergence ? 2 : div.rsiBearishDivergence ? -2 : 0;
  return result;
}
